import { useEffect, useRef, useState } from 'react';

export interface Selection<T> {
    ids: string[];
    rows: T[];
    count: number;
    isSelected: (row: T) => boolean;
    canSelect: (row: T) => boolean;
    toggle: (row: T) => void;
    toggleId: (id: string) => void;
    toggleAll: () => void;
    clear: () => void;
    set: (ids: string[]) => void;
    headerState: 'none' | 'some' | 'all';
}

export interface UseSelectionArgs<T> {
    rows: T[];
    getRowId: (row: T) => string;
    mode: false | 'single' | 'multi';
    selectable?: (row: T) => boolean;
    onChange?: (rows: T[], ids: string[]) => void;
}

function sameIds(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    return [...a].every((id) => b.has(id));
}

function assertUniqueSelectionIds<T>(
    rows: T[],
    getRowId: (row: T) => string,
): void {
    const ids = new Set<string>();
    for (const row of rows) {
        const id = getRowId(row);
        if (ids.has(id)) {
            throw new Error(
                `ics-data-table: row identities must be unique for selection. Duplicate id: "${id}".`,
            );
        }
        ids.add(id);
    }
}

export function useSelection<T>(args: UseSelectionArgs<T>): Selection<T> {
    const { rows, getRowId, mode, selectable, onChange } = args;
    if (mode !== false) {
        assertUniqueSelectionIds(rows, getRowId);
    }
    const [selected, setSelected] = useState<Set<string>>(() => new Set());
    const notified = useRef<Set<string>>(new Set());

    const notify = (nextRows: T[]): void => {
        const ids = nextRows.map(getRowId);
        notified.current = new Set(ids);
        onChange?.(nextRows, ids);
    };

    useEffect(() => {
        const kept = new Set(
            [...selected].filter((id) =>
                rows.some((row) => getRowId(row) === id),
            ),
        );
        if (!sameIds(selected, kept)) setSelected(kept);
        const keptRows = rows.filter((row) => kept.has(getRowId(row)));
        if (!sameIds(notified.current, kept)) notify(keptRows);
        // eslint-disable-next-line react/exhaustive-deps -- react only on `rows` identity, not on `selected`
    }, [rows]);

    const canSelect = (row: T): boolean =>
        mode !== false && (!selectable || selectable(row));
    const isSelected = (row: T): boolean => selected.has(getRowId(row));
    const commit = (next: Set<string>): void => {
        if (sameIds(selected, next)) return;
        setSelected(next);
        notify(rows.filter((row) => next.has(getRowId(row))));
    };

    const toggleId = (id: string): void => {
        if (mode === false) return;
        const isCurrentlySelected = selected.has(id);
        if (!isCurrentlySelected) {
            const row = rows.find((r) => getRowId(r) === id);
            if (!row || !canSelect(row)) return;
        }
        if (mode === 'single') {
            commit(isCurrentlySelected ? new Set() : new Set([id]));
            return;
        }
        const next = new Set(selected);
        if (isCurrentlySelected) next.delete(id);
        else next.add(id);
        commit(next);
    };
    const toggle = (row: T): void => {
        if (mode === false || !canSelect(row)) return;
        toggleId(getRowId(row));
    };
    const toggleAll = (): void => {
        if (mode !== 'multi') return;
        const selectableIds = rows.filter(canSelect).map(getRowId);
        const allSelected =
            selectableIds.length > 0 &&
            selectableIds.every((id) => selected.has(id));
        const next = new Set(selected);
        selectableIds.forEach((id) =>
            allSelected ? next.delete(id) : next.add(id),
        );
        commit(next);
    };
    const clear = (): void => {
        commit(new Set());
    };
    const set = (ids: string[]): void => {
        if (mode !== false)
            commit(new Set(mode === 'single' ? ids.slice(0, 1) : ids));
    };

    const selectableIds =
        mode === false ? [] : rows.filter(canSelect).map(getRowId);
    const selectedAmongSelectable = selectableIds.filter((id) =>
        selected.has(id),
    ).length;
    let headerState: 'none' | 'some' | 'all' = 'none';
    if (
        selectableIds.length > 0 &&
        selectedAmongSelectable === selectableIds.length
    )
        headerState = 'all';
    else if (selectedAmongSelectable > 0) headerState = 'some';
    const selectedRows = rows.filter((row) => selected.has(getRowId(row)));

    return {
        ids: selectedRows.map(getRowId),
        rows: selectedRows,
        count: selectedRows.length,
        isSelected,
        canSelect,
        toggle,
        toggleId,
        toggleAll,
        clear,
        set,
        headerState,
    };
}
